import type { MouseEvent } from 'react';

/** Things that already answer a press themselves, so a press on them is left alone. */
const INTERACTIVE = 'input, textarea, button, a, label, [role="button"], [role="combobox"]';

/** The field's own control: the text control on a text field, the trigger on a select. */
const CONTROL = 'input:not([type="hidden"]), textarea, [role="combobox"]';

/**
 * Hands a press on a field's bordered box to the field's control.
 *
 * Every field in this layer draws its helper text INSIDE the border but below the control, so the
 * control's hit area stops above it: pressing the helper, or the bottom inset under it, used to do
 * nothing even though it reads as part of the field. Wire this to the box's `onMouseDown`.
 *
 * A text control takes focus; a select trigger is clicked, which opens its menu the same way a
 * keyboard activation does. `preventDefault` stops the browser moving focus to the box first — and
 * also stops a drag from selecting the helper text, which is the accepted cost.
 */
export const forwardBoxPress = (event: MouseEvent<HTMLElement>) => {
  if (event.button !== 0) return;
  if (event.target instanceof Element && event.target.closest(INTERACTIVE)) return;

  const control = event.currentTarget.querySelector<HTMLElement>(CONTROL);
  if (!control || control.matches(':disabled')) return;

  event.preventDefault();
  if (control.getAttribute('role') === 'combobox') control.click();
  else control.focus();
};
